import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';

// ─── EmailJS config ────────────────────────────────────────────────────────────
// Replace these three values with the ones from your EmailJS dashboard.
// See the README comment at the bottom of this file for setup instructions.
const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';
// ──────────────────────────────────────────────────────────────────────────────

const FIELDS = [
  { name: 'from_name',  label: 'Your Name',     type: 'text',  placeholder: 'Mohammed Al Rashid', required: true },
  { name: 'from_email', label: 'Email Address', type: 'email', placeholder: 'you@example.com',    required: true },
  { name: 'subject',   label: 'Subject',        type: 'text',  placeholder: 'How can we help?',   required: true },
];

export default function ContactPage() {
  const formRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const validate = (data) => {
    const errs = {};
    if (!data.from_name.trim())  errs.from_name  = 'Name is required.';
    if (!data.from_email.trim()) errs.from_email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.from_email))
      errs.from_email = 'Enter a valid email address.';
    if (!data.subject.trim())    errs.subject    = 'Subject is required.';
    if (!data.message.trim())    errs.message    = 'Message is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(formRef.current));
    const errs = validate(formData);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setErrors({});
    setStatus('sending');
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY,
      );
      setStatus('success');
      formRef.current.reset();
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = (name) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '0.85rem 1rem',
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${errors[name] ? '#e8604a' : 'rgba(122,155,181,0.2)'}`,
    borderRadius: 6,
    color: 'var(--white)',
    fontFamily: 'var(--font-b)',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
  });

  return (
    <div style={{ paddingTop: 68 }}>

      {/* HEADER */}
      <section style={{
        padding: '5rem clamp(1rem,4vw,3rem) 3rem',
        background: 'var(--navy2)',
        borderBottom: '1px solid rgba(232,96,74,0.1)',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span className="section-tag">GET IN TOUCH</span>
          <h1 className="section-title" style={{ fontSize: 'clamp(38px,6vw,62px)', marginBottom: '1rem' }}>
            Contact <span>Us</span>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.7, maxWidth: 520 }}>
            Have a question, suggestion, or just want to say hello? Fill in the form and we'll get back to you within one business day.
          </p>
        </div>
      </section>

      {/* BODY */}
      <section style={{ padding: '4rem clamp(1rem,4vw,3rem) 6rem' }}>
        <div style={{
          maxWidth: 800, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'clamp(180px,28%,240px) 1fr',
          gap: '4rem',
          alignItems: 'start',
        }}>

          {/* LEFT — info */}
          <div>
            {[
              { icon: '✉️', label: 'Email Us',        value: 'support@hadafi.ae',  href: 'mailto:support@hadafi.ae' },
              { icon: '📍', label: 'Based In',        value: 'Dubai, UAE',          href: null },
              { icon: '🕐', label: 'Response Time',   value: '≤ 1 business day',    href: null },
            ].map(({ icon, label, value, href }) => (
              <div key={label} style={{ marginBottom: '2rem' }}>
                <div style={{ fontSize: 22, marginBottom: '0.4rem' }}>{icon}</div>
                <p style={{ fontFamily: 'var(--font-m)', fontSize: 11, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: '0.25rem' }}>{label}</p>
                {href
                  ? <a href={href} style={{ color: 'var(--coral)', fontFamily: 'var(--font-d)', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>{value}</a>
                  : <p style={{ color: 'var(--white)', fontFamily: 'var(--font-d)', fontSize: 14, fontWeight: 700 }}>{value}</p>
                }
              </div>
            ))}
          </div>

          {/* RIGHT — form */}
          <div>
            {status === 'success' ? (
              <div style={{
                padding: '2.5rem', borderRadius: 8, textAlign: 'center',
                background: 'rgba(232,96,74,0.07)',
                border: '1px solid rgba(232,96,74,0.25)',
              }}>
                <div style={{ fontSize: 48, marginBottom: '1rem' }}>✅</div>
                <h3 style={{ fontFamily: 'var(--font-d)', fontSize: 22, fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Message Sent!</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14 }}>
                  Thanks for reaching out. We'll get back to you within one business day.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  style={{
                    marginTop: '1.5rem', background: 'none',
                    border: '1px solid rgba(232,96,74,0.4)', color: 'var(--coral)',
                    fontFamily: 'var(--font-d)', fontSize: 12, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    padding: '0.6rem 1.5rem', borderRadius: 4, cursor: 'pointer',
                  }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                {FIELDS.map(({ name, label, type, placeholder }) => (
                  <div key={name}>
                    <label style={{ display: 'block', fontFamily: 'var(--font-m)', fontSize: 11, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                      {label}
                    </label>
                    <input
                      type={type}
                      name={name}
                      placeholder={placeholder}
                      style={inputStyle(name)}
                      onFocus={e => { e.target.style.borderColor = 'rgba(232,96,74,0.6)'; }}
                      onBlur={e  => { e.target.style.borderColor = errors[name] ? '#e8604a' : 'rgba(122,155,181,0.2)'; }}
                    />
                    {errors[name] && <p style={{ color: '#e8604a', fontSize: 12, marginTop: '0.3rem', fontFamily: 'var(--font-m)' }}>{errors[name]}</p>}
                  </div>
                ))}

                {/* Message */}
                <div>
                  <label style={{ display: 'block', fontFamily: 'var(--font-m)', fontSize: 11, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us what's on your mind…"
                    rows={6}
                    style={{ ...inputStyle('message'), resize: 'vertical', minHeight: 130 }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(232,96,74,0.6)'; }}
                    onBlur={e  => { e.target.style.borderColor = errors.message ? '#e8604a' : 'rgba(122,155,181,0.2)'; }}
                  />
                  {errors.message && <p style={{ color: '#e8604a', fontSize: 12, marginTop: '0.3rem', fontFamily: 'var(--font-m)' }}>{errors.message}</p>}
                </div>

                {status === 'error' && (
                  <p style={{ color: '#e8604a', fontSize: 13, fontFamily: 'var(--font-m)', padding: '0.75rem 1rem', background: 'rgba(232,96,74,0.08)', borderRadius: 4 }}>
                    Something went wrong. Please try again or email us directly at support@hadafi.ae.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  style={{
                    alignSelf: 'flex-start',
                    background: status === 'sending' ? 'rgba(232,96,74,0.5)' : 'var(--coral)',
                    color: '#fff',
                    fontFamily: 'var(--font-d)', fontSize: 13, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    padding: '0.85rem 2.5rem', borderRadius: 4,
                    border: 'none', cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.2s',
                  }}
                >
                  {status === 'sending' ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
